/* eslint-disable no-unused-vars */
import React, { useLayoutEffect, useState } from 'react'
import styles from './styles.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { ReactComponent as ClockIcon } from '../../../../../assets/icons/clock-icon.svg'
import { arrayOfTimesGeneral, convertNumberToTime } from '../../../../pages/Appointments/timeLines'
import { addNewAppointmentFromFile } from '../../../../../Redux/appointments/appointmentSlice'
import SearchableOperationEntry from './SearchableOperationEntry'
import SearchablePatientEntry from './SearchablePatientEntry'
import { toggleChildPopup } from '../../../../../Redux/popups/popupSlice'
import { useTranslation } from 'react-i18next'

const index = ({ close, data }) => {
  const { t } = useTranslation() || ((text) => text)

  const dispatch = useDispatch()
  const { operations } = useSelector((state) => state.operations)
  const { patients } = useSelector((state) => state.patients)
  const { operationId, patientId, init_date, is_ongoing } = data || {}
  const [isNew, setIsNew] = useState(!is_ongoing)

  const [selectedTime, setSelectedTime] = useState()
  const [selectedOperation, setSelectedOperation] = useState(null)
  const [selectedPatient, setSelectedPatient] = useState(null)
  const { users } = useSelector((state) => state.users)
  const [selectedDoctor, setSelectedDoctor] = useState(null)

  useLayoutEffect(() => {
    if (operations) {
      setSelectedOperation(operations.find((op) => op.id === operationId))
      setSelectedPatient(patients.find((pat) => pat.id === patientId))
    }
  }, [operations, patients])

  // Fetch users if not already loaded
  React.useEffect(() => {
    if (!users?.length && dispatch) {
      // Only fetch if not present
      if (typeof window !== 'undefined') {
        import('../../../../../Redux/users/usersSlice').then(module => {
          if (module.getAllUsers) dispatch(module.getAllUsers())
        })
      }
    }
  }, [users, dispatch])

  const handleTimeChange = (e, type) => {
    const { value } = e.target
    const newTimeObject = { ...selectedTime, [type]: value }
    const numberFrom = parseFloat(newTimeObject.from)
    const numberTo = parseFloat(newTimeObject.to)
    if (numberFrom >= numberTo) {
      if (type === 'from') {
        newTimeObject.to = numberFrom + 0.5
      } else {
        newTimeObject.from = numberTo - 0.5
      }
    }
    setSelectedTime(newTimeObject)
  }

  const submitData = (e) => {
    e.preventDefault()
    if (!isNew) {
      if (!selectedPatient) {
        alert(t('please_select_a_patient'))
        return
      }
      const prevOp = selectedPatient?.operations?.find((op) => op?.operation_id === selectedOperation?.id)
      if (!prevOp) {
        alert(t('pt_no_op'))
        return
      }
    }
    if (!selectedDoctor) {
      alert(t('please_select_a_doctor'))
      return
    }
    const formData = new FormData(e.target)
    const pricedata = isNew ? (Number(formData.get('unit_multiplier')) || 1) * Number(selectedOperation?.price) : 0
    const from = parseFloat(formData.get('time_from'))
    const to = parseFloat(formData.get('time_to'))
    const formObject = {
      date: formData.get('date'),
      from,
      to,
      fromStr: convertNumberToTime(from),
      toStr: convertNumberToTime(to),
      name: selectedOperation?.op_name || 'Untitled',
      category: selectedOperation?.category || 'N/A',
      operation_id: selectedOperation?.id || '',
      operation_name: selectedOperation?.op_name || '',
      color: selectedOperation?.color || '',
      description: formData.get('description') || '',
      patient_id: selectedPatient?.id || '',
      patient_name: selectedPatient?.full_name || '',
      doctor_id: selectedDoctor?.id || '',
      doctor_name: selectedDoctor?.displayName || '',
      paid_amount: Number(formData.get('paid_amount')) || 0,
      unit_multiplier: Number(formData.get('unit_multiplier')) || 1,
      price: pricedata,
      is_paid: Number(formData.get('paid_amount')) >= Number(pricedata),
      is_ongoing: !isNew,
      init_date,
      close
    }
    dispatch(addNewAppointmentFromFile(formObject))
  }

  const addNewPatientToggle = () => {
    dispatch(
      toggleChildPopup({
        pointer: 1,
        data: {
          category: selectedOperation?.category,
          opId: selectedOperation?.id,
          updatingFn: setSelectedPatient
        }
      })
    )
  }

  return (
    <div className={styles.popup_wrapper}>
      <div className={styles.form_header}>
        <h2 className={styles.header_text}>{t('ADD_NEW_APPOINTMENT')}</h2>
        <button className={styles.close_btn} onClick={close}>
          ✖
        </button>
      </div>
      <form className={styles.popup_from_container} onSubmit={submitData}>
        <div className={styles.form_entry}>
          <label className={styles.entry_label}>{t('appointment_date')}</label>
          <div className={styles.time_container}>
            <div className={styles.time_date_container}>
              <ClockIcon className={styles.time_indicator_icon} />
              <input
                type='date'
                id='date'
                name='date'
                defaultValue={new Date().toISOString().split('T')[0]}
                className={styles.entry_input}
                required
              />
            </div>
            <div className={styles.time_hr_container}>
              <span className={styles.time_indicator_of}>{t('from')}</span>
              <select
                className={styles.entry_input}
                id='time_from'
                name='time_from'
                value={selectedTime?.from}
                onChange={(e) => handleTimeChange(e, 'from')}
                required
              >
                {[...arrayOfTimesGeneral]?.slice(0, arrayOfTimesGeneral?.length - 1)?.map((time) => (
                  <option key={time.value} value={time.value}>
                    {time.displayValue}
                  </option>
                ))}
              </select>
              <span className={styles.time_indicator_of}>{t('to')}</span>
              <select
                id='time_to'
                name='time_to'
                value={selectedTime?.to}
                onChange={(e) => handleTimeChange(e, 'to')}
                className={styles.entry_input}
                required
              >
                {[...arrayOfTimesGeneral]?.slice(1)?.map((time) => (
                  <option key={time.value} value={time.value}>
                    {time.displayValue}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className={`${styles.form_entry} ${styles.operation_entry}`}>
          <SearchableOperationEntry
            selectedOperation={selectedOperation}
            setSelectedOperation={setSelectedOperation}
            operations={operations}
            apt={{}}
            isNew={isNew}
            setIsNew={setIsNew}
            editing={true}
            t={t}
          />
        </div>
        {selectedOperation && (
          <div className={`${styles.form_entry} ${styles.patient_entry}`}>
            <SearchablePatientEntry
              selectedPatient={selectedPatient}
              setSelectedPatient={setSelectedPatient}
              patients={patients}
              isNew={isNew}
              addNewPatientToggle={addNewPatientToggle}
              editing={true}
              t={t}
            />
          </div>
        )}
        {isNew && (
          <div className={styles.form_entry}>
            <label className={styles.entry_label} htmlFor='doctor_select'>
              {t('employee') || 'Doctor/Employee'}
            </label>
            <select
              id='doctor_select'
              name='doctor_select'
              className={styles.entry_input}
              value={selectedDoctor ? selectedDoctor.id : ''}
              onChange={e => {
                const userObj = users.find(u => u.id === e.target.value)
                setSelectedDoctor(userObj)
              }}
              required
            >
              <option value=''>-- {t('select_employee') || 'Select Doctor/Employee'} --</option>
              {users && users.map(user => (
                <option key={user.id} value={user.id}>{user.displayName || user.email || user.id}</option>
              ))}
            </select>
          </div>
        )}
        <div className={styles.form_entry}>
          <label className={styles.entry_label} htmlFor='description'>
            {t('description')}
          </label>
          <textarea className={styles.entry_input} type='text' id='description' name='description' />
        </div>
        {selectedPatient && isNew && (
          <div className={styles.form_entry}>
            <label className={styles.entry_label} htmlFor='paid_amount'>
              {t('paid_amount')}
            </label>
            <input
              className={styles.entry_input}
              type='number'
              id='paid_amount'
              name='paid_amount'
              max={Number(selectedOperation?.price)}
            />
          </div>
        )}
        <div className={styles.form_entry}>
          <button className={styles.submit_button} type='submit'>
            {data ? t('save') : t('add')}
          </button>
        </div>
      </form>
    </div>
  )
}
export default index
